import { catalogMatchConfidence, normalizeCatalogSearchText } from './catalogSearchConfidence.js'
import { fragranceSearchScore, SEARCH_SCORE } from './searchAbbrev.js'
import { queryMatchesHouse } from './houseAliases.js'

const LEADING_ARTICLE = /^(?:a|an|the)\s+/i

function withoutLeadingArticle(value) {
  return String(value || '').trim().replace(LEADING_ARTICLE, '')
}

function searchableFragrance(fragrance) {
  return {
    ...fragrance,
    name: withoutLeadingArticle(fragrance?.name),
  }
}

export function collectionWearSearchScore(fragrance, query) {
  const cleanQuery = withoutLeadingArticle(query)
  if (!normalizeCatalogSearchText(cleanQuery)) return 0

  const candidate = searchableFragrance(fragrance)

  // Check if query is a house match (e.g. "YSL" → Yves Saint Laurent)
  const isHouseMatch = queryMatchesHouse(cleanQuery, fragrance.house)
  if (isHouseMatch) {
    // High score for house matches so they appear prominently
    return SEARCH_SCORE.LEADING * 10
  }

  const prefixScore = fragranceSearchScore(candidate, cleanQuery)
  const typoConfidence = catalogMatchConfidence(cleanQuery, candidate).confidence
  const typoScore = typoConfidence >= 0.9 ? typoConfidence : 0

  // Preserve the collection search's identity/notes ordering, then use the same
  // bounded typo confidence as catalog search for rows the strict matcher misses.
  return Math.max(prefixScore * 10, typoScore)
}

function normalized(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')
}

function sameLine(a, b) {
  if (normalized(a.house) !== normalized(b.house)) return false
  const aName = normalized(a.line_name || a.name)
  const bName = normalized(b.line_name || b.name)
  if (aName === bName) return true
  const shorter = aName.length <= bName.length ? aName : bName
  const longer = shorter === aName ? bName : aName
  return shorter.split(' ').length >= 3 && longer.startsWith(`${shorter} `)
}

/**
 * Groups fragrances by line (same house + name prefix).
 * Returns array of line groups, each with a `name` and `variants` array.
 */
export function groupCollectionByLine(fragrances) {
  if (!fragrances || fragrances.length === 0) return []

  const exact = new Map()
  fragrances.forEach((frag, index) => {
    const key = `${normalized(frag.house)} ${normalized(frag.name)}`
    const current = exact.get(key) || {
      line_name: frag.name,
      house: frag.house,
      variants: [],
      first_index: index,
    }
    current.variants.push(frag)
    exact.set(key, current)
  })

  const lines = []
  for (const group of exact.values()) {
    const existing = lines.find(line => sameLine(line, group))
    if (existing) {
      existing.variants.push(...group.variants)
      existing.first_index = Math.min(existing.first_index, group.first_index)
      if (normalized(group.line_name).length < normalized(existing.line_name).length) {
        existing.line_name = group.line_name
      }
    } else {
      lines.push({ ...group })
    }
  }

  return lines
    .sort((a, b) => a.first_index - b.first_index)
    .map(group => ({
      name: group.line_name,
      house: group.house,
      variants: group.variants.sort((a, b) => {
        const aConc = normalized(a.concentration || a.concentrations?.[0] || '')
        const bConc = normalized(b.concentration || b.concentrations?.[0] || '')
        return aConc.localeCompare(bConc)
      }),
    }))
}

export function matchCollectionForWear(fragrances, query, limit = 6) {
  const cleanQuery = String(query || '').trim()
  if (!cleanQuery) return []

  return (fragrances || [])
    .map((fragrance, index) => ({
      fragrance,
      index,
      score: collectionWearSearchScore(fragrance, cleanQuery),
    }))
    .filter(candidate => candidate.score > SEARCH_SCORE.NONE)
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .slice(0, Math.max(0, limit))
    .map(candidate => candidate.fragrance)
}
